import type { IAbTestingInfo } from '@sravni/ab-testing-sdk/lib/node';
import type { ThemeNames } from '@sravni/design-system-theme';
import { ThemeName } from '@sravni/design-system-theme';
import type { IDeviceInfo } from '@sravni/koa-utils/lib/middlewares/device';
import { Notifications } from '@sravni/react-design-system';
import { AbTestingProvider, ConfigProvider, DeviceInfoProvider, ThemeProvider } from '@sravni/react-utils';
import Queue from '@sravni/utils/lib/queue';
import type { NextComponentType } from 'next';
import getConfig from 'next/config';
import React from 'react';

import {
  ADDITIONAL_PHONE_OWNER,
  ANY_AUTO_BRAND_INCOMING_ITEM,
  ANY_AUTO_MODEL_INCOMING_ITEM,
  AREAS_OF_EMPLOYMENT,
  CAR_AVAILABILITY,
  CREDIT_PURPOSES,
  CREDIT_SECURITY_TYPES,
  EDUCATION,
  EMPLOYER_TYPES,
  EMPLOYMENT_TYPES,
  INCOME_CONFIRMATIONS,
  JOB_TYPES,
  MARITAL_STATUS,
  NUMBER_OF_DEPENDENTS,
  PERIODS,
  PLEDGE_ESTATE_OWNER_RELATIONSHIP,
  PRODUCT_TYPES,
  PROPERTY_TYPE_AVAILABILITY,
  SENIORITY,
  UNEMPLOYMENT_REASONS,
} from 'constants/dictionaries';
import { APP_PAGES_NAMES } from 'constants/routes';

import { getOrCreateDeviceId } from 'shared/lib/getOrCreateDeviceId';
import { CustomCommonHead } from 'shared/ui/CustomCommonHead';
import { ErrorBoundaryFallbackProvider } from 'shared/ui/ErrorBoundaryFallbackProvider/ErrorBoundaryFallbackProvider';

import { DEFAULT_SELECTION_EXPERIMENTS_VARIANTS, setAbExperimentsInitialData } from 'entities/abExperiments';
import { setAppConfig } from 'entities/appConfig';
import {
  setContactsInfoFromUser,
  setCreditFormByCustomerData,
  setCreditFormByRestoreData,
  setCreditFormDictionaries,
  setCreditFormLocationRoutes,
  setRegionLocation,
  updateCreditFormByQueryParams,
} from 'entities/creditForm';
import { fetchRegions as fetchRegionsRequest, setCurrentLocation, setLocations } from 'entities/locations';
import { Seo, setMetadata } from 'entities/metadata';
import { setFilters, setFiltersDictionaries } from 'entities/offersFilters';
import { setPledgeAutoFormDictionaries } from 'entities/pledgeAutoForm';
import { setPledgeEstateFormDictionaries } from 'entities/pledgeEstateForm';
import { setProduct } from 'entities/product';
import { setScenario } from 'entities/scenario';
import { setAppPageName, setIsMarketplaceEnabled, setSettings } from 'entities/siteSettings';
import { setDeviceId, setUser } from 'entities/user';
import { setVitrinsOffers } from 'entities/vitrins/model/vitrins.slice';
import { whiteLabelAffIdSelector } from 'entities/whiteLabels';

import { sendFirstTouchThunk } from 'features/SendLoansFirstTouch';

import { SiteFlowWrapper } from 'widgets/SiteFlowWrapper';

import type { GlobalState } from 'app/MyApp';
import { getOrCreateStore, StoreProvider } from 'app/MyApp';
import { SentryUserSync, setUtmMarksToStore, setWLToStore } from 'app/MyApp/lib';
import { getProductTypesByQuery } from 'app/MyApp/lib/getProductTypesByQuery';
import { getThemeByAffId } from 'app/MyApp/lib/getThemeByAffId';
import { usePersistedStore } from 'app/MyApp/lib/usePersistedStore';
import { useSetCookiesOnMount } from 'app/MyApp/lib/useSetCookiesOnMount';
import { DevToolsWrapper } from 'app/MyApp/ui/DevToolsWrapper';
import type { AppInitialProps, AppProps } from 'next/app';

import { PageWrapper } from '../PageWrapper';

import styles from './MyApp.module.scss';
// eslint-disable-next-line import/no-unassigned-import
import '@sravni/design-system-theme/lib/globalStyles.css';

export const queue = new Queue({ debug: false });

const runtimeConfig = {
  gatewayUrl: getConfig().publicRuntimeConfig.gatewayUrl,
  publicUrl: getConfig().publicRuntimeConfig.publicUrl,
  environment: getConfig().publicRuntimeConfig.environment,
};

interface IProps extends AppProps {
  realPath: string;
  initialReduxState: GlobalState;
  deviceInfo: IDeviceInfo;
  theme: ThemeNames;
  abTestingInfo?: IAbTestingInfo;
  Component: FC;
  gtmKey: string;
  metaTags?: Array<{
    name: string;
    value: string;
  }>;
}

export const MyApp: NextComponentType<App.ReduxAppContext, AppInitialProps, IProps> = (props) => {
  const { Component, pageProps, initialReduxState, deviceInfo, theme, abTestingInfo, metaTags } = props;
  const reduxStore = usePersistedStore(initialReduxState);
  const { getState, dispatch } = reduxStore;
  const { user, appConfig } = getState();
  const { gtmKey } = appConfig.config;

  const { publicRuntimeConfig } = getConfig();
  const { isMarketplace } = publicRuntimeConfig || {};

  const isProduction = publicRuntimeConfig.environment === 'production';
  const isStage = publicRuntimeConfig.environment === 'stage';
  const isDevelopment = publicRuntimeConfig.environment === 'development';
  const isReactScanEnabled = !!publicRuntimeConfig.isReactScanEnabled;

  const fetchRegions = React.useCallback(async () => {
    const centers = await fetchRegionsRequest();

    dispatch(setLocations(centers));
  }, [dispatch]);

  useSetCookiesOnMount(reduxStore);

  const generateUserDeviceId = React.useCallback(() => {
    dispatch(setDeviceId(getOrCreateDeviceId()));
    dispatch(sendFirstTouchThunk({ sourceUrl: window.location.href }) as any);
  }, [dispatch]);

  const affId = whiteLabelAffIdSelector(getState());
  const CurrentTheme = getThemeByAffId(affId, theme);

  React.useEffect(() => {
    if (isDevelopment && isReactScanEnabled) {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const ReactScan = require('react-scan');
        ReactScan.scan({
          enabled: false,
          log: false,
          showToolbar: true,
        });
      } catch (e) {}
    }

    queue.push(() => generateUserDeviceId());

    if (isProduction || isStage) {
      queue.push(() => {
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.register('/service-worker.js').catch((err) => {
            console.warn('service worker registration failed', err.message); // eslint-disable-line no-console
          });
        }
      });
    }

    queue.push(() => fetchRegions());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundaryFallbackProvider>
      <CurrentTheme.Component>
        <StoreProvider store={reduxStore}>
          <SentryUserSync />
          <AbTestingProvider initialValue={abTestingInfo}>
            <DeviceInfoProvider initialInfo={deviceInfo}>
              <ThemeProvider initialTheme={CurrentTheme.themeName}>
                <ConfigProvider value={runtimeConfig}>
                  <CustomCommonHead
                    googleTmId={gtmKey}
                    userId={user?.account?.sub}
                    isProduction={isProduction}
                    withWebVisor
                  />
                  <Seo
                    isMarketplace={isMarketplace}
                    metaTags={metaTags}
                  />
                  <ErrorBoundaryFallbackProvider>
                    <PageWrapper>
                      <div className={styles.wrapper}>
                        <SiteFlowWrapper dataThemeName={theme}>
                          <DevToolsWrapper>
                            <Component {...pageProps} />
                          </DevToolsWrapper>
                        </SiteFlowWrapper>
                        <Notifications />
                      </div>
                    </PageWrapper>
                  </ErrorBoundaryFallbackProvider>
                </ConfigProvider>
              </ThemeProvider>
            </DeviceInfoProvider>
          </AbTestingProvider>
        </StoreProvider>
      </CurrentTheme.Component>
    </ErrorBoundaryFallbackProvider>
  );
};

// eslint-disable-next-line max-statements,complexity
MyApp.getInitialProps = async ({ Component, ctx }: App.ReduxAppContext) => {
  const reduxStore = getOrCreateStore();
  const isServer = typeof ctx.req !== 'undefined';
  ctx.store = reduxStore;

  const otherProps = {
    deviceInfo: { phone: false, tablet: false, isAppleMobile: false },
    theme: ThemeName.lager,
    abTestingInfo: undefined,
  };

  if (isServer && ctx.req) {
    if (ctx.req.__SCENARIO_DATA__) {
      reduxStore.dispatch(setScenario(ctx.req.__SCENARIO_DATA__));
    }
    if (ctx.req.__SITE_SETTINGS__) {
      reduxStore.dispatch(setSettings(ctx.req.__SITE_SETTINGS__));
    }
    if (ctx.req.__IS_MARKETPLACE_ENABLED__) {
      reduxStore.dispatch(setIsMarketplaceEnabled(ctx.req.__IS_MARKETPLACE_ENABLED__));
    }
    if (ctx.req.__SELECTED_LOCATION__) {
      reduxStore.dispatch(setCurrentLocation(ctx.req.__SELECTED_LOCATION__));
    }
    if (ctx.req.__APP_PAGE_NAME__) {
      reduxStore.dispatch(setAppPageName(ctx.req.__APP_PAGE_NAME__ || APP_PAGES_NAMES.UNDEFINED_PAGE));
    }

    if (ctx.req.__PRODUCT__) {
      reduxStore.dispatch(setProduct(ctx.req.__PRODUCT__));
    }

    if (ctx.req.__OFFERS__) {
      reduxStore.dispatch(setVitrinsOffers(ctx.req.__OFFERS__));
    }

    if (ctx.req.__WL__) {
      setWLToStore(ctx, reduxStore);
    } else if (ctx.req.__SEO__) {
      reduxStore.dispatch(setMetadata(ctx.req.__SEO__));
    }

    if (ctx.req.__USER__?.sub) {
      reduxStore.dispatch(setUser(ctx.req.__USER__));
      reduxStore.dispatch(setContactsInfoFromUser(ctx.req.__USER__));
    }

    if (ctx.req.__DEVICE_INFO__) {
      otherProps.deviceInfo = ctx.req.__DEVICE_INFO__;
    }

    if (ctx.req.__THEME__) {
      otherProps.theme = ctx.req.__THEME__;
    }

    if (ctx.req.__APP_CONFIG__) {
      reduxStore.dispatch(setAppConfig(ctx.req.__APP_CONFIG__));
    }

    setUtmMarksToStore(ctx, reduxStore);

    if (ctx.req.__AB_TESTING__) {
      const abTestingInfo = ctx.req.__AB_TESTING__?.abTestingInfo;
      otherProps.abTestingInfo = abTestingInfo;
      reduxStore.dispatch(
        setAbExperimentsInitialData({
          experiments: abTestingInfo?.experiments || DEFAULT_SELECTION_EXPERIMENTS_VARIANTS,
          userId: abTestingInfo?.userData?.id || null,
          isDefaultVariants: !abTestingInfo?.experiments,
        }),
      );
    }

    reduxStore.dispatch(
      setCreditFormDictionaries({
        brand: ANY_AUTO_BRAND_INCOMING_ITEM,
        brands: [ANY_AUTO_BRAND_INCOMING_ITEM, ...(ctx.req.__DICTIONARIES__?.autoBrands || [])],
        autoModels: [ANY_AUTO_MODEL_INCOMING_ITEM],
        purposes: CREDIT_PURPOSES,
        employmentTypes: EMPLOYMENT_TYPES,
        unemploymentReasons: UNEMPLOYMENT_REASONS,
        incomeConfirmations: INCOME_CONFIRMATIONS,
        seniorityTypes: SENIORITY,
        jobTypes: JOB_TYPES,
        areasOfEmployment: AREAS_OF_EMPLOYMENT,
        employerTypes: EMPLOYER_TYPES,
        educations: EDUCATION,
        maritalStatuses: MARITAL_STATUS,
        numberOfDependentsTypes: NUMBER_OF_DEPENDENTS,
        additionalPhoneOwners: ADDITIONAL_PHONE_OWNER,
        carAvailabilityTypes: CAR_AVAILABILITY,
        propertyTypeAvailabilityTypes: PROPERTY_TYPE_AVAILABILITY,
        periods: PERIODS,
      }),
    );

    reduxStore.dispatch(
      setPledgeAutoFormDictionaries({
        brands: ctx.req.__DICTIONARIES__?.autoBrands,
      }),
    );

    const productTypes = getProductTypesByQuery(ctx.query?.productType);

    if (productTypes) {
      reduxStore.dispatch(setFilters({ productTypes }));
    }

    reduxStore.dispatch(
      setFiltersDictionaries({
        purposes: CREDIT_PURPOSES,
        securityTypes: CREDIT_SECURITY_TYPES,
        productTypes: PRODUCT_TYPES,
      }),
    );

    reduxStore.dispatch(
      setPledgeEstateFormDictionaries({
        maritalStatuses: MARITAL_STATUS,
        otherOwnerRelationship: PLEDGE_ESTATE_OWNER_RELATIONSHIP,
      }),
    );

    const waybackData = ctx.req.__WAYBACK_DATA__;
    const restoredCustomer = ctx.req.__RESTORED_DATA__?.customer;
    const customerData = ctx.req.__CUSTOMER_DATA__;

    if (waybackData) {
      reduxStore.dispatch(setCreditFormByCustomerData(waybackData));
    } else if (restoredCustomer) {
      reduxStore.dispatch(setCreditFormByRestoreData(restoredCustomer));
    } else if (customerData) {
      reduxStore.dispatch(setCreditFormByCustomerData(customerData));
    }

    reduxStore.dispatch(updateCreditFormByQueryParams(ctx.query));

    if (ctx.req.__CUSTOMER_LOCATION_ROUTES__) {
      reduxStore.dispatch(setCreditFormLocationRoutes(ctx.req.__CUSTOMER_LOCATION_ROUTES__));
    }

    if (ctx.req.__CUSTOMER_REGION_LOCATION__) {
      reduxStore.dispatch(setRegionLocation(ctx.req.__CUSTOMER_REGION_LOCATION__));
    }
  }

  const pageProps = Component.getInitialProps ? await Component.getInitialProps(ctx) : {};

  return {
    initialReduxState: reduxStore.getState(),
    realPath: ctx.req?.url || '',
    gtmKey: ctx.req?.__APP_CONFIG__?.gtmKey,
    pageProps,
    metaTags: ctx.req?.__SEO__?.metaTags,
    ...otherProps,
  };
};

// export function reportWebVitals(metric: NextWebVitalsMetric) {
//   console.log(metric)
// }
